import React from "react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, forwardRef} from "react";
import { Editicon, Deleteicon } from "../../../../Components/IconComponents";
import { Eye, Filter as FilterIcon } from "lucide-react";
import { toast } from "react-toastify";
import Table from "../../../../Components/UiComponents/Table/Table";
import SelectComponent from "../../../../Components/UiComponents/SelectComponent/SelectComponent";
import Moment from "react-moment";
import Loader from "react-spinner-loader";
import {
  agencyTableData,
  columnsForExport,
} from "../../../../JsonData/AgencyConfig";
import { getData, putDataWithBody } from "../../../../Services/Services";
import { agencyListURL, agencyTypeURL, deleteAgencyURL, getThemeURL } from "../../../../Services/EndPoints";
import { useDispatch } from "react-redux";
import {
  
  getExportedColumnsData,
} from "../../../../Components/UiComponents/utils";
import { setTableData } from "../../../../CentralStore/ExportLMSTable/dataSlice";
import Confirmpopup from "../../Common/ConfirmPopup/Confirmpopup";

import { useTranslation } from "react-i18next";

const TODAY_STR = new Date().toISOString().split("T")[0];

const filterLabelStyle = {
  fontSize: "9.5px",
  fontWeight: 800,
  letterSpacing: ".3px",
  textTransform: "uppercase",
  color: "#98A2B3",
};

const filterControlStyle = {
  padding: "6px 10px",
  borderRadius: "8px",
  border: "1px solid #E4E7EC",
  fontSize: "12px",
  background: "#fff",
  color: "#1D2939",
  height: "34px",
  boxSizing: "border-box",
};

// "DD-MM-YYYY, hh:mm A" — dash-separated to stay consistent with the rest of
// the app's date formatting (en-GB's toLocaleDateString uses "/").
const formatDateDash = (value) => {
  if (!value) return "NA";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "NA";
  const pad = (n) => String(n).padStart(2, "0");
  const dateStr = `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
  const timeStr = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${dateStr}, ${timeStr}`;
};

const AgencyTable = forwardRef((props, ref) => {
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();
  const qs = require("qs");
  console.log("qs",qs)
  const [agencyData, setAgencyData] = useState([]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchData, setSearchData] = useState([]);
  const [agencyTypeOptions, setAgencyTypeOptions] = useState([]);
  const [agencyTypeFilter, setAgencyTypeFilter] = useState("");
  // Date range only takes effect once "Apply" is clicked — pending values are
  // what the inputs show, applied values are what actually filters the data.
  const [pendingDateFrom, setPendingDateFrom] = useState("");
  const [pendingDateTo, setPendingDateTo] = useState("");
  const [appliedDateFrom, setAppliedDateFrom] = useState("");
  const [appliedDateTo, setAppliedDateTo] = useState("");
  useEffect(() => {
    if (props.locationInstance && props.locationInstance.msg !== "") {
      toast.success("Agency has been created successfully.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000, // Close after 2 seconds..
      });
    }
    getAgecnyList();
    getAgencyTypeOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    sendDataToCentralStore(searchData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData]);

  const sendDataToCentralStore = (data) => {
    if (data.length > 0) {
      const columns = columnsForExport;
      const processedData = data.map((item) => ({
        ...item,
        // Format created_on as DD-MM-YYYY, hh:mm AM/PM (12-hour) so it doesn't
        // fall through to the export button's default 24-hour formatting.
        created_on: formatDateDash(item.created_on),
      }));
      dispatch(
        setTableData({
          columns,
          tableData: getExportedColumnsData(columns, processedData),
        })
      );
    }
  };
  const addNewHandler = () => {
    navigate("/planning/agency-list/create-agency");
  };

  const getAgecnyList = async () => {
    setLoader(true);
    const userProfile = await getData(getThemeURL);
    const state_id = userProfile[0]?.state_id;
    var agencyList = await getData(`${agencyListURL}/${state_id}`);
    let sortedList = agencyList.sort((a, b) => {
      return new Date(b.created_on) - new Date(a.created_on);
    });
    setLoader(false);
    setAgencyData(sortedList);
  };

  // Same Agency Type config API used by the Create Agency form, so this
  // filter's options always match what's selectable when creating an agency.
  const getAgencyTypeOptions = async () => {
    const userProfile = await getData(getThemeURL);
    const state_id = userProfile[0]?.state_id;
    const options = await getData(agencyTypeURL.replace("STATEID", state_id));
    setAgencyTypeOptions(Array.isArray(options) ? options : []);
  };

  const selectedAgencyTypeLabel = React.useMemo(
    () => agencyTypeOptions.find((o) => String(o.id) === String(agencyTypeFilter))?.value || "",
    [agencyTypeOptions, agencyTypeFilter]
  );

  const isDateRangeInvalid =
    !!pendingDateFrom && !!pendingDateTo && new Date(pendingDateFrom) > new Date(pendingDateTo);

  const handleApplyDateFilter = () => {
    if (isDateRangeInvalid) return;
    setAppliedDateFrom(pendingDateFrom);
    setAppliedDateTo(pendingDateTo);
  };

  const filteredAgencyData = React.useMemo(() => {
    return (agencyData || []).filter((a) => {
      if (agencyTypeFilter && a.agency_type !== selectedAgencyTypeLabel) return false;
      if (appliedDateFrom || appliedDateTo) {
        if (!a.created_on) return false;
        const created = new Date(a.created_on);
        if (appliedDateFrom && created < new Date(`${appliedDateFrom}T00:00:00`)) return false;
        if (appliedDateTo && created > new Date(`${appliedDateTo}T23:59:59`)) return false;
      }
      return true;
    });
  }, [agencyData, agencyTypeFilter, selectedAgencyTypeLabel, appliedDateFrom, appliedDateTo]);

  const hasActiveFilters = agencyTypeFilter !== "" || appliedDateFrom !== "" || appliedDateTo !== "";

  const clearAllFilters = () => {
    setAgencyTypeFilter("");
    setPendingDateFrom("");
    setPendingDateTo("");
    setAppliedDateFrom("");
    setAppliedDateTo("");
  };

  const columns = React.useMemo(
    () => [
      {
        Header: t("agencytableheaders.name"),
        accessor: "name",
        disableSortBy: true,
        Cell: (props) => {
          if (props.value) {
            let descriptionText = props.value;
            if (props.value.length > 50) {
              descriptionText = props.value.substring(0, 30) + "...";
            }
            return <span title={props.value}>{descriptionText}</span>;
          } else {
            return "NA";
          }
        },
      },
      {
        Header: t("agencytableheaders.agencytype"),
        accessor: "agency_type",
        Cell: (props) => {
          if (props.value) {
            let descriptionText = props.value;
            if (props.value.length > 50) {
              descriptionText = props.value.substring(0, 30) + "...";
            }
            return <span title={props.value}>{descriptionText}</span>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },
      {
        Header: t("agencytableheaders.phone"),
        accessor: "phone",
        disableSortBy: true,
        Cell: (props) => {
          if (props.value) {
            return <span title={props.value}>{props.value}</span>;
          } else {
            return "NA";
          }
        },
      },
      // {
      //   Header: "Status  ",
      //   accessor: "status",
      //   disableSortBy: true,
      //   Cell: (props) => {
      //     return <StatusHandler props={props} />;
      //   },
      // },
      {
        Header: t("agencytableheaders.createdon"),
        accessor: "created_on",
        disableSortBy: true,
        Cell: (props) => {
          if (props.value) {
            return <Moment format="DD-MM-YYYY, hh:mm A">{props.value}</Moment>;
          } else {
            return "NA";
          }
        },
      },
      {
        Header: t("agencytableheaders.action"),
        disableSortBy: true,

        Cell: (props) => {
          const [isdelete, setIsdelete] = useState(false);
          const deletePopUp = () => {
            setIsdelete(!isdelete);
          };
          const deleteTableDataById = async (id) => {
            let data = { id: id };
            var res = await putDataWithBody(deleteAgencyURL, data, {});
            if (res.status === "success") {
              getAgecnyList();
            }
            toast.success(res.msg, {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 2000, // Close after 2 seconds
            });
            setIsdelete(false);
          };

          return (
            <div className="usertable__actionsicon">
              <span
                onClick={() =>
                  navigate("/planning/agency-list/view-agency", {
                    state: { data: props.row.original },
                  })
                }
              >
                <Eye size={14} title="View" className="cursor-pointer me-2" />
              </span>

              <span
                onClick={() =>
                  navigate("/planning/agency-list/create-agency", {
                    state: {
                      data: props.row.original,
                      edit: true
                    },
                  })
                }>
                <Editicon value={props.value} className="cursor-pointer me-2" />
              </span>

              <span onClick={deletePopUp}>
                <Deleteicon
                  value={props.value}
                  className="cursor-pointer mr-1"
                />
              </span>

              {isdelete && (
                <Confirmpopup
                  className="DeltePopup"
                  show={isdelete}
                  title={t("deletepopuptext")}
                  message={t("deletepopupmessage")}
                  handleToggle={deletePopUp}
                  row={props.row}
                  name={props.row.original.name}
                  deleteRow={deleteTableDataById}
                  cancel={t("cancelbutton")}
                  action={t("actionbutton")}
                />
              )}

            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const exportData = React.useMemo(
    () =>
      (filteredAgencyData || []).map((item) => ({
        ...item,
        // Format created_on as DD-MM-YYYY, hh:mm AM/PM (12-hour) so it doesn't
        // fall through to the export button's default 24-hour formatting.
        created_on: formatDateDash(item.created_on),
      })),
    [filteredAgencyData]
  );

  return (
    <div className="create-user agency-mgmt-table">
      <style>{`
        #light .agency-mgmt-table .iccc--table th {
          background: linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%) !important;
          color: #ffffff !important;
        }
        #light .agency-mgmt-table .iccc--table tbody tr td,
        #light .agency-mgmt-table .iccc--table tbody tr th {
          border: none !important;
        }
        #light .agency-mgmt-table .iccc--table tbody tr td {
          background-color: #fff !important;
        }
        #light .agency-mgmt-table .iccc--table tbody tr:nth-child(even) td {
          background-color: #F9FAFB !important;
        }
        .agency-mgmt-table .search-form {
          position: relative;
          display: inline-flex;
          align-items: center;
        }
        .agency-mgmt-table .search-form input[type="text"] {
          height: 36px !important;
          line-height: 34px !important;
          padding: 0 40px 0 12px !important;
          border: 1px solid #E4E7EC !important;
          border-radius: 8px !important;
          font-size: 12px !important;
          box-sizing: border-box !important;
        }
        .agency-mgmt-table .search-form button {
          position: absolute !important;
          top: 0 !important;
          bottom: 0 !important;
          right: 0 !important;
          height: 36px !important;
          width: 36px !important;
          margin: 0 !important;
          padding: 0 !important;
          background-color: transparent !important;
          border: none !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .agency-mgmt-table .search-icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .agency-mgmt-table .search-icon svg {
          display: block;
          color: #2E6DA4;
        }
      `}</style>
      <Loader
        show={loader}
        type="body"
        stack="vertical"
        message="Loading Data"
      />
      <div style={{ background: "#fff", border: "1px solid #EEF1F5", borderRadius: "10px", boxShadow: "0 1px 2px rgba(16,24,40,0.04)", padding: "10px 14px", margin: "12px 12px 12px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
          <span style={{ width: "20px", height: "20px", borderRadius: "6px", background: "#EBF4FA", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <FilterIcon size={11} color="#2E6DA4" />
          </span>
          <span style={{ fontSize: "12px", fontWeight: 800, color: "#2E6DA4" }}>Filters</span>
        </div>
        <style>{`
          .agency-list-filter-bar input[type="date"] {
            position: relative;
            padding-right: 30px;
            display: inline-flex;
            align-items: center;
          }
          .agency-list-filter-bar input[type="date"]::-webkit-datetime-edit {
            display: inline-flex;
            align-items: center;
            padding: 0;
            line-height: normal;
          }
          .agency-list-filter-bar input[type="date"]::-webkit-datetime-edit-fields-wrapper {
            display: inline-flex;
            align-items: center;
          }
          .agency-list-filter-bar input[type="date"]::-webkit-calendar-picker-indicator {
            position: absolute;
            right: 8px;
            top: 50%;
            transform: translateY(-50%);
            margin: 0;
            padding: 0;
            cursor: pointer;
            opacity: 0.55;
          }
          .agency-list-filter-bar input[type="date"]::-webkit-inner-spin-button,
          .agency-list-filter-bar input[type="date"]::-webkit-clear-button {
            display: none;
          }
        `}</style>
        <div className="agency-list-filter-bar" style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 240px", maxWidth: "240px" }}>
            <label style={filterLabelStyle}>Agency Type</label>
            <SelectComponent
              id="agency_type_filter"
              value={agencyTypeFilter}
              onChange={(e) => setAgencyTypeFilter(e.target.value)}
              options={agencyTypeOptions}
              showPlaceHolder="Select Agency Type"
              placeholderSelectable={true}
              style={{
                height: "36px", lineHeight: "34px", padding: "0 28px 0 12px", borderRadius: "8px",
                border: "1px solid #E4E7EC", fontSize: "12px", background: "#fff", color: "#1D2939",
                width: "100%", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 160px" }}>
            <label style={filterLabelStyle}>Created From</label>
            <input
              type="date"
              value={pendingDateFrom}
              max={pendingDateTo && pendingDateTo < TODAY_STR ? pendingDateTo : TODAY_STR}
              onChange={(e) => setPendingDateFrom(e.target.value)}
              style={{ ...filterControlStyle, width: "100%" }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 160px" }}>
            <label style={filterLabelStyle}>Created To</label>
            <input
              type="date"
              value={pendingDateTo}
              min={pendingDateFrom || undefined}
              max={TODAY_STR}
              onChange={(e) => setPendingDateTo(e.target.value)}
              style={{ ...filterControlStyle, width: "100%" }}
            />
          </div>
          <button
            type="button"
            onClick={handleApplyDateFilter}
            disabled={!pendingDateFrom || !pendingDateTo || isDateRangeInvalid}
            title="Applies the Created From / Created To date range"
            style={{
              height: "34px", padding: "0 16px", borderRadius: "6px", fontSize: "12px", fontWeight: 600,
              border: "1px solid #2E6DA4",
              background: (!pendingDateFrom || !pendingDateTo || isDateRangeInvalid) ? "#a9c3d8" : "#2E6DA4",
              color: "#fff",
              cursor: (!pendingDateFrom || !pendingDateTo || isDateRangeInvalid) ? "not-allowed" : "pointer",
            }}
          >
            Apply
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              style={{ height: "34px", padding: "0 12px", borderRadius: "8px", border: "1px solid #E4E7EC", background: "#F9FAFB", color: "#475467", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
            >
              Clear Filters
            </button>
          )}
        </div>
        {isDateRangeInvalid && (
          <div style={{ marginTop: "8px", fontSize: "11px", color: "#DC2626", fontWeight: 600 }}>
            "Created From" date must be on or before "Created To" date.
          </div>
        )}
      </div>
      <div className="datatable__container">
        {agencyTableData && (
          <Table
            data={filteredAgencyData}
            setSearchData={setSearchData}
            columns={columns}
            numberOfRows={10}
            searchPlaceholder="Search by Agency Name, Agency Type, Phone Number"
            searchInputStyle={{ width: "20em" }}
            addNewHandler={addNewHandler}
            showExport={false}
            showExcelExport={true}
            exportData={exportData}
            exportColumns={columnsForExport}
            exportFileName="AgencyManagement"
            showFilter={false}
            addButtonLabel={t("createagencyaddbuttonlabel")}
            showReset={false}
            onExpand={false}
            rowCount={filteredAgencyData.length} // Pass rowCount to the Table component
            headerTableHeadStyle={{ background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)", color: "#FFFFFF" }}
            tableStyle={{
              border: "1px solid #EEF1F5",
              borderRadius: "10px",
              boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
            }}
          />
        )}
      </div>
    </div>
  );
});

export default AgencyTable;
